import { DateTimeUtils } from "@common/utils";
import { defaultTo } from "lodash"
import { DateTime } from "luxon";
import { CommunicationType } from "./Enums";
import { InvoiceListingModel } from "./InvoiceListingModel";
import { InvoicePaymentModel, IInvoicePaymentModel } from "./InvoicePaymentModel";
import { InvoiceSalesItemModel, IInvoiceSalesItemModel } from "./InvoiceSalesItemModel";
import { IModel, Model, ModelRecord } from "./Model"
import { IAddClientEmailRequest } from "./requests/AddClientEmailRequest";
import { VisitModel } from "./VisitModel";
import { IInvoiceNoteModel, InvoiceNoteModel } from "./InvoiceNoteModel";
import { InvoiceDiscrepancyReasonModel } from "./InvoiceDiscrepancyReasonModel";

export interface IInvoiceModel extends IModel {
    invoiceId: number;
    dateDueUTC: string;
    dateFullyPaidUTC: string | undefined;
    clientId: number;
    clientFirstName: string;
    clientLastName: string;
    petNames: string;
    amount: number;
    amountDue: number;
    serviceAmount: number;
    productAmount: number;
    licenseFeeAmount: number;
    vetcoPrescriptionAmount: number;
    discountAmount: number;
    taxPercentage: number;
    subtotalPreDiscountsAmount: number;
    subtotalPostDiscountsAmount: number;
    taxAmount: number;
    refundAmount: number;
    servicesOnlyDiscountAmount: number;
    productsOnlyDiscountAmount: number;
    servicesRebateAmount: number | undefined;
    productsRebateAmount: number | undefined;
    totalRebateAmount: number | undefined;
    nonTaxableFeeAmount: number | undefined;
    taxableFeeAmount: number | undefined;
    totalFeeAmount: number | undefined;
    totalAOVAmount: number | undefined;
    salesPackageId: number | undefined;
    salesPackageName: string | undefined;
    salesPackagePrice: number | undefined;
    salesPackageDiscountAmount: number | undefined;
    salesPackageInvoicePrice: number | undefined;
    taxableTotalAmount: number;
    nonTaxableTotalAmount: number;
    veterinarianId: number;
    vetFirstName: string;
    vetLastName: string;
    visitId: number;
    dateCreated: string;
    userCreated: string | undefined;
    dateUpdated: string | undefined;
    userUpdated: string | undefined;
    invoiceDiscrepancyReasonId: number | undefined;
    invoiceDiscrepancyReason: InvoiceDiscrepancyReasonModel | undefined;
    invoicePayments: IInvoicePaymentModel[];
    invoiceSalesItems: IInvoiceSalesItemModel[];
    notes: IInvoiceNoteModel[];
}

export class InvoiceModel extends Model implements ModelRecord<IInvoiceModel> {
    invoiceId: number;
    dateDueUTC: DateTime;
    dateFullyPaidUTC: DateTime | undefined;
    clientId: number;
    clientFirstName: string;
    clientLastName: string;
    petNames: string;
    amount: number;
    amountDue: number;
    serviceAmount: number;
    productAmount: number;
    licenseFeeAmount: number;
    vetcoPrescriptionAmount: number;
    discountAmount: number;
    taxPercentage: number;
    subtotalPreDiscountsAmount: number;
    subtotalPostDiscountsAmount: number;
    taxAmount: number;
    refundAmount: number;
    servicesOnlyDiscountAmount: number;
    productsOnlyDiscountAmount: number;
    servicesRebateAmount: number | undefined;
    productsRebateAmount: number | undefined;
    totalRebateAmount: number | undefined;
    nonTaxableFeeAmount: number | undefined;
    taxableFeeAmount: number | undefined;
    totalFeeAmount: number | undefined;
    totalAOVAmount: number | undefined;
    salesPackageId: number | undefined;
    salesPackageName: string | undefined;
    salesPackagePrice: number | undefined;
    salesPackageDiscountAmount: number | undefined;
    salesPackageInvoicePrice: number | undefined;
    taxableTotalAmount: number;
    nonTaxableTotalAmount: number;
    veterinarianId: number;
    vetFirstName: string;
    vetLastName: string;
    visitId: number;
    dateCreated: DateTime;
    userCreated: string | undefined;
    dateUpdated: DateTime | undefined;
    userUpdated: string | undefined;
    invoiceDiscrepancyReasonId: number | undefined;
    invoiceDiscrepancyReason: InvoiceDiscrepancyReasonModel | undefined;
    invoicePayments: InvoicePaymentModel[];
    invoiceSalesItems: InvoiceSalesItemModel[];
    notes: IInvoiceNoteModel[];

    constructor(params: IInvoiceModel) {
        super(params);
        this.invoiceId = params.invoiceId;
        this.dateDueUTC = DateTime.fromISO(params.dateDueUTC, { zone: 'UTC' });
        this.dateFullyPaidUTC = params.dateFullyPaidUTC ? DateTime.fromISO(params.dateFullyPaidUTC, { zone: 'UTC' }) : undefined;
        this.clientId = params.clientId
        this.clientFirstName = params.clientFirstName;
        this.clientLastName = params.clientLastName;
        this.petNames = params.petNames;
        this.amount = params.amount;
        this.amountDue = params.amountDue;
        this.serviceAmount = params.serviceAmount;
        this.productAmount = params.productAmount;
        this.licenseFeeAmount = params.licenseFeeAmount;
        this.vetcoPrescriptionAmount = params.vetcoPrescriptionAmount;
        this.discountAmount = params.discountAmount;
        this.taxPercentage = params.taxPercentage;
        this.subtotalPreDiscountsAmount = params.subtotalPreDiscountsAmount;
        this.subtotalPostDiscountsAmount = params.subtotalPostDiscountsAmount;
        this.taxAmount = params.taxAmount;
        this.refundAmount = params.refundAmount;
        this.servicesOnlyDiscountAmount = params.servicesOnlyDiscountAmount;
        this.productsOnlyDiscountAmount = params.productsOnlyDiscountAmount;
        this.servicesRebateAmount = params.servicesRebateAmount;
        this.productsRebateAmount = params.productsRebateAmount;
        this.totalRebateAmount = params.totalRebateAmount;
        this.nonTaxableFeeAmount = params.nonTaxableFeeAmount;
        this.taxableFeeAmount = params.taxableFeeAmount;
        this.totalFeeAmount = params.totalFeeAmount;
        this.totalAOVAmount = params.totalAOVAmount;
        this.salesPackageId = params.salesPackageId;
        this.salesPackageName = params.salesPackageName;
        this.salesPackagePrice = params.salesPackagePrice;
        this.salesPackageDiscountAmount = params.salesPackageDiscountAmount;
        this.salesPackageInvoicePrice = params.salesPackageInvoicePrice;
        this.taxableTotalAmount = params.taxableTotalAmount;
        this.nonTaxableTotalAmount = params.nonTaxableTotalAmount;
        this.veterinarianId = params.veterinarianId;
        this.vetFirstName = params.vetFirstName;
        this.vetLastName = params.vetLastName;
        this.visitId = params.visitId;
        this.dateCreated = DateTimeUtils.getAsDateTimeUTC(params.dateCreated)!
        this.userCreated = params.userCreated ?? undefined;
        this.dateUpdated = params.dateUpdated == null ? undefined : DateTimeUtils.getAsDateTimeUTC(params.dateUpdated)!;
        this.userUpdated = params.userUpdated ?? undefined;
        this.invoiceDiscrepancyReasonId = params.invoiceDiscrepancyReasonId;
        this.invoiceDiscrepancyReason = params.invoiceDiscrepancyReason ? new InvoiceDiscrepancyReasonModel(params.invoiceDiscrepancyReason) : undefined;
        this.invoicePayments = (params.invoicePayments ?? []).map(si => new InvoicePaymentModel(si));
        this.invoiceSalesItems = (params.invoiceSalesItems ?? []).map(si => new InvoiceSalesItemModel(si));
        this.notes = (params.notes ?? []).map(n => new InvoiceNoteModel(n));
    }

    get doctorsName(): string {
        return `Dr. ${this.vetFirstName} ${this.vetLastName}`;
    }

    toListingModel(hospitalId?: number, hospitalName?: string, timeZone?: string): InvoiceListingModel {
        return InvoiceListingModel.fromInvoiceModel(this, defaultTo(hospitalId, 0), defaultTo(hospitalName, ""), defaultTo(timeZone, ""));
    }

    toClientEmailRequest(visit: VisitModel, communicationType: CommunicationType): IAddClientEmailRequest {
        return {
            visitId: visit.visitId,
            clientId: this.clientId!,
            visitSalesItemId: undefined,
            petId: visit.petId,
            invoiceId: this.invoiceId!,
            emailAddress: visit.client.emailAddress,
            salesDate: visit.visitDate!.toLocaleString(DateTime.DATE_SHORT),
            communicationTypeId: communicationType,
            communicationTypeName: CommunicationType.displayName(communicationType),
            storageContentUrl: undefined,
            includeRabiesCertificate: false
        }
    }
}
